import { Link } from 'react-router-dom';
import { Gauge, ShieldAlert, Users, Presentation } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

const capabilities = [
  {
    icon: Gauge,
    iconColor: 'text-primary-600 dark:text-primary-400',
    iconBg: 'bg-primary-100 dark:bg-primary-900/30',
    title: 'Delivery Velocity',
    description:
      'See exactly why cycle time spiked last sprint - whether it\'s review queues, oversized PRs, or blocked merges. Pinpoint it by team, repo, or week.',
    proof: 'On average, 39% of cycle time is just waiting for review. Most teams don\'t know until they see it.',
    screenshot: '/images/screenshots/dashboard-team-overview-full.webp',
    screenshotAlt: 'Team dashboard showing velocity metrics and cycle time breakdown',
    screenshotPosition: 'center 15%',
    featureLinks: [
      { slug: 'cycle-time', label: 'Cycle Time' },
      { slug: 'dora-metrics', label: 'DORA Metrics' },
      { slug: 'forecasting', label: 'Forecasting' },
      { slug: 'deployment-analytics', label: 'Deployments' },
      { slug: 'velocity', label: 'Velocity' },
      { slug: 'sprint-retro', label: 'Sprint Retro' },
      { slug: 'issue-lead-time', label: 'Issue Lead Time' },
    ],
  },
  {
    icon: ShieldAlert,
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    title: 'Code Quality & Risk',
    description:
      'Find the files that cause 80% of your incidents before the next deploy. See which modules only one person understands. Get alerted before risky changes ship.',
    proof: '1 in 5 PRs merged without approval. 39K+ file hotspots identified across teams.',
    screenshot: '/images/screenshots/file-hotspots-full.webp',
    screenshotAlt: 'File hotspots view showing high-risk code areas and knowledge silos',
    screenshotPosition: 'center 8%',
    featureLinks: [
      { slug: 'file-hotspots', label: 'File Hotspots' },
      { slug: 'knowledge-silos', label: 'Knowledge Silos' },
      { slug: 'risky-changes', label: 'Risk Detection' },
      { slug: 'alerts', label: 'Alerts' },
    ],
  },
  {
    icon: Users,
    iconColor: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    title: 'Team Health',
    description:
      'See how review load is shared across the team so you can rebalance before anyone burns out. Surface workload imbalances that standups miss - measured at the team level, to support people, not rank them.',
    proof: '"We found review load was piling on one person. Rebalanced it in a week." - VP Eng',
    screenshot: '/images/screenshots/awards-leaderboard-full.webp',
    screenshotAlt: 'Team health leaderboard showing developer collaboration and review patterns',
    screenshotPosition: 'center 20%',
    featureLinks: [
      { slug: 'review-network', label: 'Review Network' },
      { slug: 'developer-comparison', label: 'Developer Insights' },
      { slug: 'review-insights', label: 'Review Quality' },
      { slug: 'space-framework', label: 'SPACE Framework' },
      { slug: 'developer-surveys', label: 'Surveys' },
      { slug: 'awards', label: 'Awards' },
    ],
  },
  {
    icon: Presentation,
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    title: 'Engineering Intelligence',
    description:
      'Board-ready summaries of where engineering capacity actually went last quarter. Track AI tooling ROI and investment allocation across teams without spreadsheets.',
    proof: '57% of engineering time is invisible to leadership. Stop reporting from gut feel.',
    screenshot: '/images/screenshots/executive-summary.png',
    screenshotAlt: 'Executive summary view showing investment allocation and AI tools ROI',
    screenshotPosition: 'center top',
    featureLinks: [
      { slug: 'executive-summary', label: 'Executive Summary' },
      { slug: 'investment-allocation', label: 'Investment Allocation' },
      { slug: 'ai-tools-roi', label: 'AI Tools ROI' },
      { slug: 'benchmarks', label: 'Benchmarks' },
      { slug: 'integrations', label: 'Integrations' },
    ],
  },
];

const getStepDelay = (index: number) => {
  const delays = ['animation-delay-0', 'animation-delay-150', 'animation-delay-300', 'animation-delay-500'];
  return delays[index] || 'animation-delay-0';
};

export default function CapabilitiesSection() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={sectionRef}
      className="py-12 bg-gray-50 dark:bg-gray-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-10 ${
            isInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            What You'll See in 15 Minutes
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Connect GitHub and instantly unlock these insights
          </p>
        </div>

        {/* Capability Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {capabilities.map((cap, index) => (
            <div
              key={cap.title}
              className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
                isInView ? `animate-fade-in-up ${getStepDelay(index)}` : 'opacity-0'
              }`}
            >
              {/* Screenshot Thumbnail */}
              <div className="aspect-[4/3] bg-gray-900 overflow-hidden">
                <img
                  src={cap.screenshot}
                  alt={cap.screenshotAlt}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity"
                  style={{ objectPosition: cap.screenshotPosition }}
                />
              </div>

              {/* Card Content */}
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${cap.iconBg}`}>
                    <cap.icon className={`w-5 h-5 ${cap.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {cap.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {cap.description}
                </p>
                {cap.proof && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 font-medium italic">
                    {cap.proof}
                  </p>
                )}
                {cap.featureLinks && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    {cap.featureLinks.map((link) => (
                      <Link
                        key={link.slug}
                        to={`/features/${link.slug}`}
                        className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
